import {
  ExistingApplicationPage,
  LoadingPage,
} from '@resurs-internal/deposits-app-components';
import { CountryContext } from 'contexts/CountryContext';
import { isEmpty } from 'helpers/isEmpty';
import { useProducts } from 'hooks/useProducts';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router';

import { isAuthenticated, sendGTMApplicationStart } from '@/helpers';
import { useApplication, useCampaignCode } from '@/hooks';
import {
  fetchApplication,
  initApplication,
  resetApplication,
  saveApplication,
} from '@/services';
import {
  ApplicationStatus,
  type DepositProduct,
  type InitApplicationResponse,
  type InitiateConflictResponse,
  type NotOldEnoughResponse,
  type ProductBoundaryExceededResponse,
  ProductBoundaryType,
} from '@/types/consumer-deposit-application-service';

const isNewApplicationResponse = (
  application:
    | InitApplicationResponse
    | InitiateConflictResponse
    | NotOldEnoughResponse
    | ProductBoundaryExceededResponse,
): application is InitApplicationResponse => {
  return (
    (application as InitApplicationResponse)?.termsConditionsAccepted !==
    undefined
  );
};

export const NewApplicationView = () => {
  const navigate = useNavigate();
  const { country } = useContext(CountryContext);
  const { product } = useOutletContext<{ product: DepositProduct }>();
  const { products } = useProducts(country);
  const { appendCampaignCode, campaignCode } = useCampaignCode();
  const [existingProduct, setExistingProduct] = useState<
    DepositProduct | undefined
  >(undefined);
  const [existingCampaignCode, setExistingCampaignCode] = useState<
    string | null
  >(null);

  const sendGTMStart = useCallback(
    (applicationId: string, productVariant: string) => {
      sendGTMApplicationStart({
        applicationId,
        country,
        productVariant,
      });
    },
    [country],
  );

  const [hasExistingApplication, setHasExistingApplication] =
    useState<boolean>(false);
  const [applicationId, setApplicationId] = useState<string>('');
  const { application } = useApplication(applicationId);
  const hasInitialized = useRef(false);

  const handleReset = () => {
    if (!product || !applicationId || isEmpty(applicationId)) {
      navigate('/error');

      return;
    }

    resetApplication(applicationId)
      .then((result) => {
        if (result && isNewApplicationResponse(result)) {
          sendGTMStart(result.applicationId, product.name);

          const defaultApplication = {
            depositGuaranteeAccepted: false,
            productCode: product.code,
            status: ApplicationStatus.PROCESSING,
            termsConditionsAccepted: result.termsConditionsAccepted,
            ...(existingCampaignCode && { campaignCode: existingCampaignCode }),
          };

          saveApplication(result.applicationId, defaultApplication)
            .then(() => {
              navigate(result.applicationId, { replace: true });
            })
            .catch(() => {
              navigate('/error');
            });
        } else {
          navigate('/error');
        }
      })
      .catch((error) => {
        console.error('Reset application failed', error);
        navigate('/error');
      });
  };

  const handleContinue = () => {
    if (!existingProduct) {
      navigate('/error');

      return;
    }
    application.setProductCode(existingProduct.code);
    navigate(applicationId, { replace: true });
  };

  useEffect(() => {
    if (hasInitialized.current) return;

    if (!product) {
      navigate('/error');
    }

    if (product && isAuthenticated() && products.length > 0) {
      hasInitialized.current = true;
      initApplication(product.code, campaignCode)
        .then((result) => {
          if (result && Object.keys(result).includes('applicationId')) {
            setApplicationId((result as InitApplicationResponse).applicationId);
            if (isNewApplicationResponse(result)) {
              sendGTMStart(result.applicationId, product.name);
              const defaultApplication = {
                depositGuaranteeAccepted: false,
                productCode: product.code,
                status: ApplicationStatus.PROCESSING,
                termsConditionsAccepted: result.termsConditionsAccepted,
                ...(campaignCode && { campaignCode }),
              };

              saveApplication(result.applicationId, defaultApplication)
                .then(() => {
                  navigate(result.applicationId);
                })
                .catch(() => {
                  navigate('/error');
                });
            } else {
              const resultAsConflict = result as InitiateConflictResponse;

              if (!resultAsConflict?.resumable) {
                navigate(`${resultAsConflict.applicationId}/pending`, {
                  state: { code: '409' },
                });

                return;
              } else {
                fetchApplication(resultAsConflict.applicationId)
                  .then((existingApplication) => {
                    const productCode =
                      existingApplication.application.productCode;

                    if (products.length > 0) {
                      const foundProduct = products.find(
                        (p) => p.code === productCode,
                      );

                      setExistingCampaignCode(
                        existingApplication.application?.campaignCode ?? null,
                      );
                      setHasExistingApplication(true);
                      setExistingProduct(foundProduct);
                    }
                  })
                  .catch(() => {
                    navigate('/error');
                  });
              }
            }
          } else {
            if ((result as NotOldEnoughResponse)?.code === '451') {
              navigate('/underage');
            } else if (
              (result as ProductBoundaryExceededResponse)?.boundary?.type ===
              ProductBoundaryType.MAX_ACCOUNTS
            ) {
              navigate(appendCampaignCode('max-accounts/' + product.code));
            } else {
              navigate('/error');
            }
          }
        })
        .catch(() => {
          navigate('/error');
        });
    }
  }, [
    navigate,
    product,
    products,
    sendGTMStart,
    appendCampaignCode,
    campaignCode,
  ]);

  if (hasExistingApplication && applicationId && product) {
    return (
      <ExistingApplicationPage
        continueWithProduct={handleContinue}
        newProduct={product}
        previousProduct={existingProduct ?? product}
        resetWithNewProduct={handleReset}
      />
    );
  }

  return <LoadingPage country={country} />;
};
